import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { tuple } from 'src/replicant/utils/types';
import {
  getPetAssetKey,
  speciesAssetsRatio,
  speciesChatbot,
  SpeciesType,
} from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { buildSender } from '../sender';

/**
 * Assets for state messages
 */
export const stateAssets = {
  'background/status/dying': 'private/chatbot/status/dying.jpg',
  'background/status/dead': 'private/chatbot/status/dead.jpg',
  'background/status/dirty': 'private/chatbot/status/dirty.jpg',
  'background/status/hungry': 'private/chatbot/status/hungry.jpg',
  'background/status/lonely': 'private/chatbot/status/lonely.jpg',
  'background/status/evolving': 'private/chatbot/status/evolve.jpg',
  'background/status/scared': 'private/chatbot/status/scared.jpg',
  'background/status/sick': 'private/chatbot/status/sick.jpg',
  'background/status/tooth_decay': 'private/chatbot/status/tooth_decay.jpg',
  'background/status/pee': 'private/chatbot/status/pee.jpg',
  'background/status/very_pee': 'private/chatbot/status/very_pee.jpg',
  'background/status/very_dirty': 'private/chatbot/status/very_dirty.jpg',
  'background/status/very_hungry': 'private/chatbot/status/very_hungry.jpg',
  'background/status/very_lonely': 'private/chatbot/status/very_lonely.jpg',
  'background/status/want_to_go_out': 'private/chatbot/status/want_out.jpg',
};

export const statusVariants = tuple(
  'dying',
  'dead',
  'dirty',
  'hungry',
  'lonely',
  'evolving',
  'scared',
  'sick',
  'tooth_decay',
  'pee',
  'very_pee',
  'very_dirty',
  'very_hungry',
  'very_lonely',
  'want_to_go_out',
);

export type statusType = (typeof statusVariants)[number];

// Species mood to status message map
const statusToMood: Record<statusType, SpeciesType> = {
  dead: 'verysad',
  dying: 'dying',
  dirty: 'sad',
  hungry: 'sad',
  lonely: 'sad',
  evolving: 'normal',
  scared: 'sad',
  sick: 'sad',
  very_pee: 'verysad',
  tooth_decay: 'sad',
  pee: 'sad',
  very_dirty: 'verysad',
  very_hungry: 'verysad',
  very_lonely: 'verysad',
  want_to_go_out: 'normal',
};

// Add here different bottom offset for type, default offset 32%
const bottomOffset: Partial<Record<statusType, string>> = {
  hungry: '38%',
  pee: '39%',
};

/**
 * Create state message
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const statusMessage = createFlexMessageTemplate(
  SB.object({
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja']),
    petName: SB.string(),
    status: SB.tuple(statusVariants),
  }),
  (data, payload, api) => {
    const altText = getLocaleConfig(data.locale).status[data.status].text;
    const mood = statusToMood[data.status];
    const icon = api.getAssetPath(getPetAssetKey(data.species, mood));
    const normal = api.getAssetPath(getPetAssetKey(data.species));
    const bottom = bottomOffset[data.status] ?? '30%';

    const template = createFlexContainer(altText, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(altText, payload),
      body: {
        type: 'box',
        layout: 'vertical',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`background/status/${data.status}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.51:1',
            gravity: 'center',
          },
          {
            type: 'box',
            layout: 'vertical',
            position: 'absolute',
            width: '100%',
            height: '100%',
            offsetBottom: bottom,
            alignItems: 'center',
            justifyContent: 'flex-end',
            contents: [
              {
                type: 'box',
                layout: 'vertical',
                contents: [
                  {
                    type: 'image',
                    url: icon,
                    aspectRatio: '286:213', // Use fixed aspect ratio for pet icon.
                    size: 'full',
                  },
                ],
                width: '45%',
                height: '55%',
              },
            ],
          },
        ],
        paddingAll: '0px',
      },
    });

    // Add sender to message
    template['sender'] = buildSender({ name: data.petName, iconUrl: normal });

    return template;
  },
);
